import React, { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  Divider,
  ListItemIcon,
  Typography,
  ListItemText,
  useTheme,
} from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import InventoryIcon from "@mui/icons-material/Inventory"
import AddIcon from "@mui/icons-material/Add"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import CreditCardIcon from "@mui/icons-material/CreditCard"
import PostAddIcon from "@mui/icons-material/PostAdd"
import RequestQuoteIcon from "@mui/icons-material/RequestQuote"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit"
import PersonIcon from "@mui/icons-material/Person"
import LogoutIcon from "@mui/icons-material/Logout"
import LanguageIcon from "@mui/icons-material/Language"
import PaletteIcon from "@mui/icons-material/Palette"
import ChatIcon from "@mui/icons-material/Chat"
import CheckIcon from "@mui/icons-material/Check"

import logo from "../assets/images/logo.png"
import { useThemeContext, THEME_OPTIONS, THEME_LABELS } from "../theme/ThemeContext"
import NotificationBell from "../components/mui/NotificationBell"
import { useDispatch, useSelector } from "react-redux"
import { toggleChatPanel } from "../store/realtimeChat/actions"
import { selectTotalUnreadMessages, selectChatPanelOpen } from "../store/realtimeChat/reducer"

const MuiHeader = ({ drawerWidth, onMenuClick, isMobile }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const headerHeight = theme.layoutConfig?.headerHeight || 70
  const { currentTheme, setTheme } = useThemeContext()
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [profileAnchor, setProfileAnchor] = useState(null)
  const [langAnchor, setLangAnchor] = useState(null)
  const [themeAnchor, setThemeAnchor] = useState(null)

  const totalUnread = useSelector(selectTotalUnreadMessages)
  const chatPanelOpen = useSelector(selectChatPanelOpen)

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")

  const handleNavigation = (path) => {
    navigate(path)
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen?.()
      setIsFullscreen(false)
    }
  }

  const handleProfileClick = (event) => {
    setProfileAnchor(event.currentTarget)
  }

  const handleProfileClose = () => {
    setProfileAnchor(null)
  }

  const handleLogout = () => {
    handleProfileClose()
    navigate("/logout")
  }

  const handleProfile = () => {
    handleProfileClose()
    navigate("/profile")
  }

  const handleLangClick = (event) => {
    setLangAnchor(event.currentTarget)
  }

  const handleLangClose = () => {
    setLangAnchor(null)
  }

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng)
    handleLangClose()
  }

  const handleThemeClick = (event) => {
    setThemeAnchor(event.currentTarget)
  }

  const handleThemeClose = () => {
    setThemeAnchor(null)
  }

  const changeTheme = (themeName) => {
    setTheme(themeName)
    handleThemeClose()
  }

  const languages = [
    { code: "en", label: "English" },
    { code: "gr", label: "Greek" },
    { code: "de", label: "German" },
  ]

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        backgroundColor: "background.paper",
        color: "text.primary",
        boxShadow: "0 0.75rem 1.5rem rgba(18, 38, 63, 0.03)",
      }}
    >
      <Toolbar sx={{ minHeight: `${headerHeight}px !important`, justifyContent: "space-between" }}>
        {/* Left Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Mobile menu button - Only visible on mobile */}
          {isMobile && (
            <IconButton
              color="inherit"
              onClick={onMenuClick}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Mobile logo - Only visible on mobile */}
          {isMobile && (
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <img src={logo} alt="FlexFlow" height="35" />
            </Box>
          )}

          {/* Quick action buttons */}
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title={t("Orders")}>
              <IconButton
                color="inherit"
                onClick={() => handleNavigation("/orders")}
                size="small"
              >
                <InventoryIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("Create Order")}>
              <IconButton
                color="inherit"
                onClick={() => handleNavigation("/createorder")}
                size="small"
              >
                <AddIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("Shipping")}>
              <IconButton
                color="inherit"
                onClick={() => handleNavigation("/shipping")}
                size="small"
              >
                <LocalShippingIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("Create Payment")}>
              <IconButton
                color="inherit"
                onClick={() => handleNavigation("/createpayment")}
                size="small"
              >
                <CreditCardIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("Create Invoice")}>
              <IconButton
                color="inherit"
                onClick={() => handleNavigation("/invoice-create")}
                size="small"
              >
                <PostAddIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("Make a Quote")}>
              <IconButton
                onClick={() => window.open("/quote", "_blank")}
                size="small"
                sx={{
                  bgcolor: "#3b82f6",
                  color: "#fff",
                  ml: 0.5,
                  "&:hover": { bgcolor: "#2563eb" },
                }}
              >
                <RequestQuoteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Chat */}
          <Tooltip title={t("Team Chat")}>
            <IconButton
              color={chatPanelOpen ? "primary" : "inherit"}
              onClick={() => dispatch(toggleChatPanel())}
              size="small"
            >
              <Badge badgeContent={totalUnread} color="error" sx={{ "& .MuiBadge-badge": { fontSize: "0.65rem", minWidth: 16, height: 16 } }}>
                <ChatIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <NotificationBell />

          {/* Language Selector */}
          <Tooltip title={t("Language")}>
            <IconButton color="inherit" onClick={handleLangClick} size="small">
              <LanguageIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={langAnchor}
            open={Boolean(langAnchor)}
            onClose={handleLangClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {languages.map((lang) => (
              <MenuItem
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                selected={i18n.language === lang.code}
              >
                {lang.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Fullscreen Toggle */}
          <Tooltip title={isFullscreen ? t("Exit Fullscreen") : t("Fullscreen")}>
            <IconButton
              color="inherit"
              onClick={toggleFullscreen}
              size="small"
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>

          {/* Profile Menu */}
          <Tooltip title={t("Profile")}>
            <IconButton onClick={handleProfileClick} size="small" sx={{ ml: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "primary.main",
                  fontSize: "0.875rem",
                }}
              >
                {authUser?.username?.charAt(0)?.toUpperCase() || "U"}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={handleProfileClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{
              sx: { minWidth: 180 },
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" noWrap>
                {authUser?.username || "User"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {authUser?.email || ""}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              {t("Profile")}
            </MenuItem>
            <MenuItem onClick={handleThemeClick}>
              <ListItemIcon>
                <PaletteIcon fontSize="small" />
              </ListItemIcon>
              {t("Themes")}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t("Logout")}
            </MenuItem>
          </Menu>

          {/* Theme Selector Menu */}
          <Menu
            anchorEl={themeAnchor}
            open={Boolean(themeAnchor)}
            onClose={handleThemeClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "left", vertical: "top" }}
            PaperProps={{
              sx: { minWidth: 160 },
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 600 }}>
                {t("Select Theme")}
              </Typography>
            </Box>
            <Divider />
            {Object.entries(THEME_OPTIONS).map(([key, value]) => (
              <MenuItem
                key={value}
                onClick={() => changeTheme(value)}
                selected={currentTheme === value}
              >
                <ListItemIcon>
                  {currentTheme === value ? <CheckIcon fontSize="small" color="primary" /> : <Box sx={{ width: 20 }} />}
                </ListItemIcon>
                <ListItemText>{THEME_LABELS[value]}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default MuiHeader
